import moment from 'moment';
import Formato from "./db/Formato.js";
import FormatoOffline from "./db/FormatoOffline.js";

const self = {moment: moment};

// Receptor con el que se invocan las reglas: si una regla usa `this`, ve este objeto vacío y
// congelado en vez de EntityUtils vivo (con el que `this.ejecutarRegla = () => true` reescribiría
// el evaluador y todas las reglas siguientes pasarían sin evaluarse).
const RECEPTOR_REGLAS = Object.freeze({});

// Los nombres de pestanas e items los escribe el inspector, y acaban dentro del HTML de los
// dialogos de la ficha: se escapan en el momento de construir cualquier mensaje que los lleve.
function escaparHtml(texto) {
    return String(texto == null ? '' : texto)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

var EntityUtils = {
    getMoment: function (fecha) {
        return moment(fecha, 'YYYY-MM-DD');
    },
    /**
     * Vamos a indicarle a la función auditar que debe evaluar las reglas de auditoría vinculadas a la acción indicada:
     * - 'sincronizar': se evaluarán todas las reglas de auditoría con evalOnSync == true
     * - 'finalizar': se evaluarán todas las reglas de auditoría con evalOnFinish == true
     * devolveremos true si el expediente cumple todas las reclas y false en caso contrario
     */
    cumpleReglas: function (accion, entity, offline, callback = (cumple)=>{return cumple;}) {
        // si accion no es ni 'sincronizar' ni 'finalizar', lo evaluaremos todo
        if (accion != 'sincronizar' && accion != 'finalizar') {
            accion = 'todo';
        }
        if (!(entity.formato && entity.formato.id)) {
            let msg = 'No se puede auditar un expediente sin formato';
            entity.listErrorsForLabel.push({ valor: 'Acción', errores: [{ nombre: 'Acción', error: msg }] });
            app.dialog.alert("msg").open();
            callback(false);
            return false;
        }
        EntityUtils.removeAllErrors(entity);
        var dbAdapter = offline ? FormatoOffline : Formato;
        dbAdapter.get({ itemId: entity.formato.id }).then(function (res) {
            console.log('- Formato: ', res);
            let respuesta = true;
            if (res.activarReglasAuditorias == true) {
                entity.formatoOrigen = res;
                if(!entity.formatoOrigen.listadoCondicionesAuditoria) {
                    entity.formatoOrigen.listadoCondicionesAuditoria = [];
                }
                entity.formato = JSON.parse(JSON.stringify(res));
                if(!entity.formato.listadoCondicionesAuditoria) {
                    entity.formato.listadoCondicionesAuditoria = [];
                }
                const reglas = EntityUtils.reglasAplicables(entity.formato, accion);
                if (reglas.length) {
                    entity.listErrorsForLabel = [];
                    respuesta = EntityUtils.evaluarReglas(reglas, entity);
                }
            }
            // Las pestanas se auditan SIEMPRE que las haya, tenga o no reglas el formato raiz.
            // Antes se salia aqui en cuanto el raiz no tenia reglas activas, y con eso los
            // cuadros y transformadores quedaban sin auditar aunque su subformato si las tuviera.
            return EntityUtils.cumpleReglasSubformatos(accion, entity, dbAdapter).then(function (cumplenItems) {
                callback(respuesta && cumplenItems);
            });
        }).catch(function (err) {
            console.log(err);
            callback(true);
        });
    },
    /**
     * Las reglas de un formato que tocan evaluar para una accion, listas para `evaluarReglas`.
     * La misma criba para el formato raiz y para cada subformato: activas, con regla, y filtradas
     * por `evalOnSync` / `evalOnFinish` segun se sincronice o se finalice.
     */
    reglasAplicables: function (formato, accion) {
        let reglas = [...(formato.listadoCondicionesAuditoria || []).filter(c => c.regla && c.activa).map(c => { c.regla.tipoRegla = c.tipoRegla; c.regla.evalOnSync = c.evalOnSync; c.regla.evalOnFinish = c.evalOnFinish; return c.regla; })];
        switch (accion) {
            case 'sincronizar':
                reglas = reglas.filter(c => (!c.evalOnSync && !c.evalOnFinish) || c.evalOnSync == true);
                break;
            case 'finalizar':
                reglas = reglas.filter(c => (!c.evalOnSync && !c.evalOnFinish) || c.evalOnFinish == true);
                break;
            default:
                break;
        }
        return reglas;
    },
    /**
     * Evalua las reglas de cada subformato sobre cada item de su pestana.
     *
     * Una regla de subformato recibe como `expediente` el ITEM (un cuadro, un transformador),
     * con sus caracteristicas y comprobaciones aplanadas igual que las de un expediente, mas dos
     * cosas que un item no lleva por si solo: `tab` (nombre, tipo y formato de la pestana) y
     * `padre`, el expediente raiz completo —sin sus pestanas— para las reglas que cruzan el item
     * con el conjunto. Los errores se cuelgan del item, de sus caracteristicas y comprobaciones,
     * igual que en el raiz; `erroresDeItems` los recoge despues con la etiqueta de su item.
     *
     * La copia del subformato que viaja dentro del expediente (`tab.formato`) no lleva reglas:
     * hay que leer el formato de verdad, una vez por subformato aunque lo usen varias pestanas.
     * Devuelve una promesa con `true` si todos los items cumplen.
     */
    cumpleReglasSubformatos: function (accion, entity, dbAdapter) {
        const conElementos = (entity.tabs || []).filter(t => t && t.items && t.items.length);
        let cumplen = true;
        // Un aviso informativo en el expediente sobre una pestana que no se ha podido auditar:
        // cuenta como "no cumple" para que la ficha lo ensene, pero al ser informativo no
        // bloquea la sincronizacion ni la finalizacion (`incumpleReglaCritica` solo mira los
        // bloqueantes). Un defecto de datos que el inspector no puede arreglar desde aqui tiene
        // que verse, no parar el trabajo.
        const avisarPestana = function (tab, motivo) {
            entity.listErrorsForLabel = entity.listErrorsForLabel || [];
            entity.listErrorsForLabel.push({ valor: 'Pestana ' + escaparHtml(tab.nombre || ''), errores: [{
                nombre: 'SUBFORMATO', tipoRegla: 'informativa',
                error: 'La pestana "' + escaparHtml(tab.nombre || '') + '" ' + motivo + ': sus elementos no se han podido auditar.',
            }] });
            cumplen = false;
        };
        // Una pestana con elementos que no dice de que subformato es (PowerSync puede
        // reconstruirla sin `formato` si el codigo venia nulo) no se puede auditar.
        conElementos.filter(t => !(t.formato && (t.formato.id || t.formato.codigo))).forEach(function (t) {
            avisarPestana(t, 'no identifica su subformato');
        });
        const tabs = conElementos.filter(t => t.formato && (t.formato.id || t.formato.codigo));
        if (!tabs.length) {
            return Promise.resolve(cumplen);
        }
        // El subformato se busca por id si lo hay y por codigo si no: los expedientes promovidos
        // desde PowerSync reconstruyen `tab.formato` solo con el codigo, y esas pestanas tienen
        // cuadros igual. `getList` con filtro va contra el buscador (online) o contra la base
        // local (offline, donde el filtro es un like), asi que se elige el de codigo EXACTO.
        const leerSubformato = function (formato) {
            if (formato.id) {
                return dbAdapter.get({ itemId: formato.id });
            }
            return dbAdapter.getList({ filter: { codigo: formato.codigo } }).then(function (res) {
                return ((res && res.list) || []).find(f => f.codigo === formato.codigo) || null;
            });
        };
        // El padre va sin sus pestanas (ya no hacen falta) y con una referencia ligera a su
        // formato: `entity.formato` es a estas alturas el formato COMPLETO con sus reglas, y
        // eso no tiene por que viajar dentro de cada item.
        const padre = JSON.parse(JSON.stringify(Object.assign({}, entity, {
            tabs: undefined,
            formatoOrigen: undefined,
            formato: entity.formato ? { id: entity.formato.id, codigo: entity.formato.codigo, nombre: entity.formato.nombre } : undefined,
        })));
        const subformatos = {};
        let cadena = Promise.resolve();
        tabs.forEach(function (tab) {
            cadena = cadena.then(function () {
                const claveSub = tab.formato.id || ('codigo:' + tab.formato.codigo);
                // Arranca desde una promesa resuelta para que un adaptador que LANCE (en vez de
                // rechazar) caiga tambien en el catch de esta pestana: si escapara, tumbaria la
                // cadena entera y el catch general de `cumpleReglas` respondería "cumple".
                const cargado = Promise.resolve().then(function () {
                    return subformatos[claveSub]
                        ? subformatos[claveSub]
                        : leerSubformato(tab.formato).then(function (sub) { subformatos[claveSub] = sub; return sub; });
                });
                return cargado.then(function (sub) {
                    if (!sub) {
                        // Hay codigo pero no hay formato con el (p. ej. la base local no lo tiene).
                        avisarPestana(tab, 'tiene un subformato que no se ha encontrado (' + escaparHtml(tab.formato.codigo || tab.formato.id) + ')');
                        return;
                    }
                    if (sub.activarReglasAuditorias != true) {
                        return;
                    }
                    const reglas = EntityUtils.reglasAplicables(sub, accion);
                    if (!reglas.length) {
                        return;
                    }
                    const formatoSub = JSON.parse(JSON.stringify(sub));
                    // Los metadatos de la pestana salen del subformato LEIDO, no de la copia que
                    // viaja en el expediente: esa copia puede traer solo el codigo (PowerSync), y
                    // una regla que mire `expediente.tab.formato.nombre` no puede recibir nada por eso.
                    const pestana = { nombre: tab.nombre, tipo: tab.tipo, formato: { codigo: sub.codigo || tab.formato.codigo, nombre: sub.nombre || tab.formato.nombre } };
                    tab.items.forEach(function (item) {
                        if (!item) { return; }
                        item.listErrorsForLabel = [];
                        const ok = EntityUtils.evaluarReglas(reglas, item, null, null, {
                            padre: padre, tab: pestana, formato: formatoSub, formatoOrigen: sub,
                        });
                        cumplen = cumplen && ok;
                    });
                }).catch(function (err) {
                    // Que no se pueda leer un subformato no debe impedir sincronizar, pero tampoco
                    // pasar en silencio: sin sus reglas evaluadas, la inspeccion seguiria como si
                    // cumpliera. Se deja el aviso informativo, igual que con la pestana sin formato.
                    console.log('No se ha podido auditar la pestana ' + tab.nombre, err);
                    avisarPestana(tab, 'tiene un subformato que no se ha podido cargar');
                });
            });
        });
        return cadena.then(function () { return cumplen; });
    },
    /**
     * Los errores de auditoria de todos los items de todas las pestanas, cada uno con la etiqueta
     * de su item ("[CUADROS · CGBT] ") para que se sepa de que cuadro es. Un item sin errores no
     * aporta nada.
     */
    erroresDeItems: function (entity) {
        const errores = [];
        (entity.tabs || []).forEach(function (tab, iTab) {
            (tab && tab.items || []).forEach(function (item, iItem) {
                if (!item) { return; }
                const etiqueta = '[' + (tab.nombre ? escaparHtml(tab.nombre) + ' · ' : '') + (escaparHtml((item.nombre || '').trim()) || ('item ' + (iItem + 1))) + '] ';
                const anotar = function (e) { if (e) { errores.push(Object.assign({ etiqueta: etiqueta }, e)); } };
                (item.listErrorsForLabel || []).forEach(campo => (campo.errores || []).forEach(anotar));
                EntityUtils.getUltimasHijas(item.caracteristicas).forEach(c => (c.listErrorsForLabel || []).forEach(anotar));
                EntityUtils.getUltimasHijas(item.comprobaciones).forEach(c => (c.listErrorsForLabel || []).forEach(anotar));
            });
        });
        return errores;
    },
    /**
     * Congela un objeto y todo lo que cuelga de el. Sobre una copia recien hecha con JSON no hay
     * ciclos, y el corte por `isFrozen` evita repetir trabajo con los nodos compartidos.
     */
    congelarEnProfundidad: function (valor) {
        if (valor && typeof valor === 'object' && !Object.isFrozen(valor)) {
            Object.freeze(valor);
            Object.keys(valor).forEach(k => EntityUtils.congelarEnProfundidad(valor[k]));
        }
        return valor;
    },
    /**
     * Ejecuta el codigo de una regla con lo justo a la vista.
     *
     * Antes era un `eval(condition)` a secas, que se ejecuta en el ambito de `evaluarReglas`: la
     * regla veia `entity` —el expediente VIVO—, `caracts`, `compros`, `EntityUtils`, `Formato` y
     * todo lo importado en el modulo. Con `new Function` el ambito es el de esta funcion y nada
     * mas; el `eval` de dentro es directo, asi que el valor de la regla sigue siendo el de su
     * ultima expresion y no cambia como se evaluan las reglas de siempre.
     *
     * `moment` y `self` van como parametros a proposito: dentro de un `new Function`, `self` es
     * el global del navegador y no el del modulo, asi que las reglas que usan `self.moment(...)`
     * se quedarian sin el. Se pasan tambien `entity`, `caracts` y `compros` —copias congeladas—
     * porque alguna regla antigua podria nombrarlos: asi no revientan, pero tampoco escriben.
     *
     * Se invoca SIEMPRE con `.call(RECEPTOR_REGLAS, ...)`: llamada como metodo, `this` dentro de
     * la regla seria EntityUtils y una regla podria reescribir el propio evaluador.
     */
    ejecutarRegla: new Function('expediente', 'entity', 'moment', 'self', 'codigoDeLaRegla',
        'var caracts = expediente.caracteristicas;'
        + 'var compros = expediente.comprobaciones;'
        + 'return eval(codigoDeLaRegla);'),
    // devuelve true si no hay errores y false en caso contrario
    /**
     * `extras` solo lo trae la evaluacion de un ITEM de subformato: `padre` y `tab` se anaden a
     * lo que ve la regla, y `formato`/`formatoOrigen` son el subformato, que es de donde salen
     * el tipo de cada regla y los valores afectados (el item no lleva formato propio, y colgarle
     * uno se guardaria con el expediente).
     */
    evaluarReglas(reglasAfectadas, entity, caracteristica, tipo, extras) {
        const formatoDeReglas = (extras && extras.formato) || entity.formato;
        const formatoOrigen = (extras && extras.formatoOrigen) || entity.formatoOrigen;
        const caracts = EntityUtils.getUltimasHijas(entity.caracteristicas);
        const compros = EntityUtils.getUltimasHijas(entity.comprobaciones);
        if (caracteristica && !caracteristica.erroresAuditoria) {
            caracteristica.erroresAuditoria=[];
        }
        if (!entity.listErrorsForLabel) {
            entity.listErrorsForLabel=[];
        }
        // Lo que ve la regla es una copia COMPLETA e independiente del expediente, y congelada.
        //
        // Antes se copiaba `entity` pero acto seguido se le pegaban las hojas del ORIGINAL:
        // `getUltimasHijas` devuelve los mismos objetos, no copias. Asi que una regla que hiciera
        // `expediente.caracteristicas[0].valor = 'X'` cambiaba el expediente de verdad —el que se
        // esta editando— y el cambio se guardaba. Una regla AUDITA el expediente; no lo toca.
        //
        // El aplanado se hace ahora sobre la copia, y se congela el arbol entero. `caracts` y
        // `compros` siguen siendo los nodos vivos, pero solo para colgarles los errores despues:
        // eso lo hace el motor, no la regla.
        const copiaExpediente = JSON.parse(JSON.stringify(entity));
        // Se aplanan TODOS los arboles que una regla puede mirar, no solo los de la raiz: los
        // items de las pestanas para las reglas del formato raiz, y `padre` para las de un
        // subformato. Una regla no deberia tener que saber aplanar. Mismo criterio que el motor
        // del servidor y que la validacion de simecal-ai.
        const aplanarNodo = function (n) {
            if (n && typeof n === 'object') {
                n.caracteristicas = EntityUtils.getUltimasHijas(n.caracteristicas);
                n.comprobaciones = EntityUtils.getUltimasHijas(n.comprobaciones);
            }
            return n;
        };
        (copiaExpediente.tabs || []).forEach(t => (t && t.items || []).forEach(aplanarNodo));
        const expediente = Object.assign({}, copiaExpediente, {
            caracteristicas: EntityUtils.getUltimasHijas(copiaExpediente.caracteristicas),
            comprobaciones: EntityUtils.getUltimasHijas(copiaExpediente.comprobaciones),
        });
        if (extras) {
            if (extras.padre) { expediente.padre = aplanarNodo(JSON.parse(JSON.stringify(extras.padre))); }
            if (extras.tab) { expediente.tab = JSON.parse(JSON.stringify(extras.tab)); }
        }
        EntityUtils.congelarEnProfundidad(copiaExpediente);
        EntityUtils.congelarEnProfundidad(expediente);
        let erroresValor = { errores: [] };
        if (tipo && tipo == 'VALOR' && caracteristica) {
            erroresValor = entity.listErrorsForLabel.filter(err => err.valor == caracteristica.nombre)[0];
            if (!erroresValor) {
                entity.listErrorsForLabel.push({ valor: caracteristica.nombre, errores: [] });
                erroresValor = entity.listErrorsForLabel.filter(err => err.valor == caracteristica.nombre)[0];
            }
            if (!erroresValor.errores) {
                erroresValor.errores = [];
            }
        }
        let hayErrores = false;
        reglasAfectadas.forEach(c => {
            const caractsErrores = [
                ...caracts.filter(c2 => c2.erroresAuditoria && c2.erroresAuditoria.find(e => e.nombre == c.numeroRegla)),
                ...compros.filter(c2 => c2.erroresAuditoria && c2.erroresAuditoria.find(e => e.nombre == c.numeroRegla)),
                ...entity.listErrorsForLabel.filter(e => e.errores && e.errores.find(e2 => e2.nombre == c.numeroRegla))
            ];
            const condition = c.regla;
            let error = { nombre: c.numeroRegla };
            try {
                const res = EntityUtils.ejecutarRegla.call(RECEPTOR_REGLAS, expediente, copiaExpediente, moment, self, condition);
                if (typeof res != 'boolean' || !res) {
                    hayErrores = true;
                    error.error = res;
                    // vamos a añadir el error a las características afectadas y en caso de estar yá asociado, lo actualizamos
                    c.caracteristicasAfectadas?.forEach(caaf => {
                        let ca2 = caracts.find(ca => ca.nombre == caaf);
                        EntityUtils.addErrorAuditoria(ca2, error, false, entity, formatoDeReglas);
                    });
                    // vamos a añadir el error a las comprobaciones afectadas y en caso de estar yá asociado, lo actualizamos
                    c.comprobacionesAfectadas?.forEach(coaf => {
                        let co2 = compros.find(co => co.codigo == coaf);
                        EntityUtils.addErrorAuditoria(co2, error, false, entity, formatoDeReglas);
                    });
                    // vamos a añadir el error a los valores afectados y en caso de estar yá asociado, lo actualizamos
                    c.valoresAfectados?.forEach(vaaf => {
                        let va2 = EntityUtils.getValoresAfectados(formatoDeReglas).find(v => v.nombre == vaaf);
                        EntityUtils.addErrorAuditoria(va2, error, true, entity, formatoDeReglas);
                    });
                    // Una regla de ELEMENTO que falla sin campo al que colgarse (sin afectados, o con
                    // afectados que el item no tiene) se anota en el propio item: si no, `erroresDeItems`
                    // no la ve, el bloqueante no bloquea y la inspeccion se sincroniza igual. En el
                    // raiz ese hueco existe desde siempre y se deja como esta a proposito: cerrarlo ahi
                    // empezaria a bloquear sincronizaciones que hoy pasan, y eso se decide aparte.
                    if (extras) {
                        const conDestino = (c.caracteristicasAfectadas || []).some(n => caracts.find(ca => ca.nombre == n))
                            || (c.comprobacionesAfectadas || []).some(n => compros.find(co => co.codigo == n))
                            || (c.valoresAfectados || []).some(n => EntityUtils.getValoresAfectados(formatoDeReglas).find(v => v.nombre == n));
                        if (!conDestino) {
                            EntityUtils.addErrorAuditoria({ nombre: 'Elemento' }, error, true, entity, formatoDeReglas);
                        }
                    }
                } else if (caracteristica) {
                    EntityUtils.removeErrors(error, caracteristica, caractsErrores, entity);
                }
            } catch (e) {
                console.log(c.numeroRegla + ' - ' + c.regla, e);
                c.error = JSON.stringify(e.stack).replaceAll('\\n', '<br/>');
                // Actualizaremos datos de la regla actual también en  formatoOrigen
                let ro = ((formatoOrigen && formatoOrigen.listadoCondicionesAuditoria) || []).find(co => co.numero == c.numeroRegla);
                if (ro) {
                    try { ro.regla.error = JSON.stringify(e.stack).replaceAll('\\n', '<br/>') } catch (e1) { }
                    try { ro.regla.error = e } catch (e1) { }
                }
                if (caracteristica) {
                    EntityUtils.removeErrors(error, caracteristica, caractsErrores);
                }
            }
        });
        return !hayErrores;
    },
    addErrorAuditoria(ca2, error, esValor = false, entity, formato = entity.formato) {
        if (ca2) {
            const condiciones = (formato && formato.listadoCondicionesAuditoria) || [];
            let tipoRegla = (error && error.nombre && condiciones.find(c=>c.numero == error.nombre)) ? condiciones.find(c=>c.numero == error.nombre).tipoRegla : '';
            let errObjeto = { nombre: error.nombre, error: error.error, tipoRegla: tipoRegla };
            if (esValor) {
                let errV = entity.listErrorsForLabel.filter(e => e.valor == ca2.nombre)[0];
                if (!errV) {
                    entity.listErrorsForLabel.push({ valor: ca2.nombre, errores: [] });
                    errV = entity.listErrorsForLabel.filter(e => e.valor == ca2.nombre)[0];
                }
                const error2 = errV.errores.filter(e => e.nombre == error.nombre)[0];
                if (!error2) {
                    errV.errores.push(errObjeto);
                }
            } else {
                if (!ca2.erroresAuditoria) {
                    ca2.erroresAuditoria=[];
                }
                if (!ca2.listErrorsForLabel) {
                    ca2.listErrorsForLabel=[];
                }
                const error2 = ca2.erroresAuditoria.filter(e => e.nombre == error.nombre)[0];
                if (!error2) {
                    ca2.erroresAuditoria.push(errObjeto);
                }
                const error2_1 = ca2.listErrorsForLabel.filter(e => e.nombre == error.nombre)[0];
                if (!error2_1) {
                    ca2.listErrorsForLabel.push(errObjeto);
                }
            }
        }
    },
    removeErrors(error, caracteristica, caractsErrores, entity) {
        if (error) {
            let idx = caracteristica.erroresAuditoria.map(e => e.nombre).indexOf(error.nombre);
            if (idx > -1) {
                caracteristica.erroresAuditoria.splice(idx, 1);
            } else {
                let error2 = entity.erroresAuditoria.filter(e => e.valor == error.nombre).forEach(e => {
                    let idxE = e.errores.indexOf(error);
                    if (idxE > -1) {
                        e.errores.splice(idxE, 1);
                    }
                });
            }
            idx = caracteristica.listErrorsForLabel.map(e => e.nombre).indexOf(error.nombre);
            if (idx > -1) {
                caracteristica.listErrorsForLabel.splice(idx, 1);
            } else {
                let error2 = entity.listErrorsForLabel.filter(e => e.valor == error.nombre).forEach(e => {
                    let idxE = e.errores.indexOf(error);
                    if (idxE > -1) {
                        e.errores.splice(idxE, 1);
                    }
                });
            }
        }
        (caractsErrores || []).forEach(cError => {
            if (cError.erroresAuditoria) {
                const err = cError.erroresAuditoria.find(e => e.nombre == (error ? error.nombre : error.nombre));
                if (err) {
                    cError.erroresAuditoria.splice(cError.erroresAuditoria.indexOf(err), 1);
                }
                const err1 = cError.listErrorsForLabel.find(e => e.nombre == (error ? error.nombre : error.nombre));
                if (err1) {
                    cError.listErrorsForLabel.splice(cError.listErrorsForLabel.indexOf(err1), 1);
                }
            } else if (cError.errores) {
                const err = cError.errores.find(e => e.nombre == (error ? error.nombre : error.nombre));
                if (err) {
                    cError.errores.splice(cError.errores.indexOf(err), 1);
                }
            }
        });
    },
    removeAllErrors(entity) {
        // borramos la lista de errores de los valores
        entity.listErrorsForLabel = [];
        // borramos la lista de errores de las características
        EntityUtils.getUltimasHijas(entity.caracteristicas).forEach(c => {
            c.erroresAuditoria = [];
            c.listErrorsForLabel = [];
        });
        // borramos la lista de errores de las comprobaciones
        EntityUtils.getUltimasHijas(entity.comprobaciones).forEach(c => {
            c.erroresAuditoria = [];
            c.listErrorsForLabel = [];
        });
        // y los de los items de las pestanas, que se auditan con las reglas de su subformato
        (entity.tabs || []).forEach(tab => (tab && tab.items || []).forEach(item => {
            if (item && typeof item === 'object') {
                EntityUtils.removeAllErrors(item);
            }
        }));
    },
    getUltimasHijas: function (coleccion) {
        let nodosHoja = [];
        if(coleccion) {
            coleccion.forEach(c => {
                if (c.hijas && c.hijas.length) {
                    const hijas = EntityUtils.getUltimasHijas(c.hijas);
                    nodosHoja = nodosHoja.concat(hijas);
                } else {
                    nodosHoja.push(c);
                }
            })
        }
        return nodosHoja;
    },
    getCaracteristicasAfectadas: function (formato) {
        let caracteristicas = new Set(formato.listadoCondicionesAuditoria.filter(c => c.regla).flatMap(c => c.regla.caracteristicasAfectadas));
        // Convwertir a objeto
        caracteristicas = [...caracteristicas].map(v => {
            return { nombre: v };
        });
        return caracteristicas;
    },
    getComprobacionesAfectadas: function (formato) {
        let comprobaciones = new Set(formato.listadoCondicionesAuditoria.filter(c => c.regla).flatMap(c => c.regla.comprobacionesAfectadas));
        // Convwertir a objeto
        comprobaciones = [...comprobaciones].map(v => {
            return { nombre: v };
        });
        return comprobaciones;
    },
    getValoresAfectados: function (formato) {
        let valores = new Set(formato.listadoCondicionesAuditoria.filter(c => c.regla).flatMap(c => c.regla.valoresAfectados));
        // Convwertir a objeto
        valores = [...valores].map(v => {
            return { nombre: v };
        });
        return valores;
    },
    formateoError(entity, nombreCampo) {
        let errorHTML;
        if(entity.listErrorsForLabel) {
            errorHTML = entity.listErrorsForLabel.filter(e => e.valor == nombreCampo)
                .flatMap(e => e.errores)
                .map(e => '<span class="regla-' + e.tipoRegla + '">' + e.nombre + '.- ' + e.error + '</span>').join(' ');
        }
        return errorHTML;
    },
    tooltipError(entity, nombreCampo) {
        let errorTxt = '';
        if(entity.listErrorsForLabel) {
            errorTxt = entity.listErrorsForLabel.filter(e => e.valor == nombreCampo)
                .flatMap(e => e.errores)
                .map(e => e.nombre + '.- ' + e.error).join('\n');
        }
    }
}

// Nadie reasigna métodos de EntityUtils en caliente (verificado), así que se congela: ni una
// regla ni un descuido pueden reemplazar el evaluador o sus auxiliares.
Object.freeze(EntityUtils);

export default EntityUtils;